/**
 * RowWrites — the ONE optimistic ledger of a row's own fields.
 *
 * Every write of a row's own fields — `label`, `icon`, `heightPx`,
 * `formula`, `timeScale`, `lookupKey` and the `attributes` bag (alias props,
 * display attributes, renderer and fork flags included) — enters this ledger,
 * whichever facade issued it: the SDK's `SheetRow.Handle` and
 * `SheetRow.Joined.Handle` (checked and durable), and the rows-side writers
 * (`Sheet` display attributes, `AliasRow` props, `FormulaDataSource`
 * expressions, the fork / reset / renderer copy-on-write sends). One ledger
 * per client (`SheetModelCache.rowWrites`), keyed by `RowLocalId`, so a row
 * held by two sheets, by a sheet and a standalone `client.rows.open` facade,
 * or by no sheet at all has one pending model.
 *
 * The rules are the generic engine's (`Ledger`, `./ledger.ts`): `RowWrites`
 * is its row-field instance — the row-field strategy below and the row
 * spelling of its surface.
 *
 * ## The projection
 *
 * A row's entry is its `base` — the confirmed value of every field a write
 * touched (read from the row's display when a write first touches the field,
 * advanced by accepted writes and authoritative re-reads) — and its writes in
 * flight, in issue order. The row SHOWS the projection:
 *
 *     projection = base ⊕ write₁ ⊕ … ⊕ writeₙ          (issue order)
 *
 * A write is a change, not a snapshot: a field set whole (`update`), or an
 * attribute merge (`mergeAttributes`, `setProp`, `setDisplayAttr`) laid over
 * the projected bag. So two writes of one bag never carry each other's stale
 * copy, and a refusal removes exactly its own change.
 *
 * ## One settle rule (field-set scoped)
 *
 * The server applies row PATCHes in arrival order and replaces each field a
 * PATCH names (`attributes` as a whole bag). Every body is therefore built at
 * SEND time from the projection, restricted to the write's own fields
 * (`attributes`: the whole projected bag), so a body always carries every
 * older write of those fields still in flight:
 *
 * - accepted: the write's body is what the server holds for its fields. It
 *   folds into `base` once every older write settled (an older write still
 *   in flight keeps a later-landed one out of `base`); an older write whose
 *   fields the accepted body covered no longer decides them by its own
 *   acceptance.
 * - refused: the write leaves the ledger and its fields go back to the
 *   projection of the writes that remain — never to a value captured when it
 *   was issued, so a refused older write never erases a newer one that
 *   landed. When a LATER send carried its fields (every body is the
 *   projection, so a PATCH sent after the write was issued carried it), the
 *   surviving projection of those fields is sent again (the take-back
 *   PATCH), so the server does not keep what the SDK took back.
 * - an authoritative re-read replaces `base` (`beginRead` / `endRead`); the
 *   writes accepted during it that were SENT after it opened lay over it in
 *   issue order (a write sent before it reached the server first: the
 *   read's answer holds it), the writes in flight keep replaying over it.
 *
 * ## One show, one emit
 *
 * The ledger writes every display copy of the row from the one projection
 * (`Display`: the rows-side `RowMetadataStore` of every sheet holding the row,
 * and the SDK's `SheetRow.raw`), silently, then emits ONE invalidation per
 * field whose projected value changed, stamped with the write's actor (a
 * take-back with the write's correction actor), in this order:
 * `RowHeightChanged`, `RowLegendChanged` (label, icon), `RowDataChanged` with
 * field `formula`,
 * `timeScale`, `lookupKey`, or the attribute key that changed (and the extra
 * fields a display reports, e.g. an alias's `props.<name>`). A field whose
 * projected value did not change announces nothing.
 */

import type { Option } from "../core/option.ts";
import { Json, type JsonObject } from "../core/json.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import type { SheetsNetwork } from "../wire/rows-network.ts";
import {
  type Actor,
  type InvalidationSink,
  RowDataChanged,
  RowHeightChanged,
  RowLegendChanged,
} from "../tx/invalidation.ts";
import { RowWriteError, requestRejection } from "../core/errors.ts";
import { Ledger } from "./ledger.ts";

/** The row PATCH route every body of this ledger is sent to. */
const ROUTE = "/api/rows/{rowKey}";

/** A take-back PATCH's change: none (its body is the projection it is sent with). */
const NO_CHANGE: RowWrites.Change = Object.freeze({ set: Object.freeze({}), attributes: null });

/**
 * The row-field instance of the one ledger (`Ledger`): a row's own fields
 * merge whole (`RowWrites.apply`: `attributes` deep / shallow merges), a
 * body is the projection restricted to a write's fields (`restrict`), and a
 * show announces one event per changed field (`RowHeightChanged`,
 * `RowLegendChanged`, `RowDataChanged` per data field / attribute key /
 * display extra — attribute keys deferred on a quiet begin).
 */
const ROW_FIELDS: Ledger.Strategy<RowWrites.Types> = {
  empty: () => ({}),
  merge: (fields, change) => RowWrites.apply(fields, change),
  fieldsOf: (change) => RowWrites.fieldsOf(change),
  body: (fields, only) => RowWrites.restrict(fields, only),
  adopt: (fields, from, only) => {
    let out = fields;
    for (const f of only) out = { ...out, [f]: from[f] ?? null };
    return out;
  },
  overlay: (under, over) => ({ ...under, ...defined(over) }),
  takeBack: () => NO_CHANGE,
  delta: (before, after) => RowWrites.delta(before, after),
  events: (row, show, actor, quiet) => {
    const { before, after, delta, extra } = show;
    const set = delta.set;
    const now: (RowLegendChanged | RowHeightChanged | RowDataChanged)[] = [];
    if (set.heightPx !== undefined) now.push(new RowHeightChanged(actor, row));
    if (set.label !== undefined || set.icon !== undefined) {
      const label = after.label ?? null;
      now.push(new RowLegendChanged(actor, row, set.label !== undefined ? before.label ?? null : label, label));
    }
    for (const field of ["formula", "timeScale", "lookupKey"] as const) {
      if (set[field] !== undefined) now.push(new RowDataChanged(actor, row, field));
    }
    const attributeFields = [...(delta.attributes?.keys ?? []), ...extra];
    if (!quiet) {
      for (const field of attributeFields) now.push(new RowDataChanged(actor, row, field));
    }
    return { now, deferred: attributeFields };
  },
  deferred: (row, name, actor) => new RowDataChanged(actor, row, name),
  wrap: (err, context) => RowWrites.wrap(err, context),
};

/**
 * The client's row-field ledger: an instance of `Ledger` over
 * `RowWrites.Types` with the row-field strategy. Its surface is the
 * engine's, spelled for rows (`row` for the key, `quietAttributes` for a
 * quiet begin, an answer always carrying its refetch hint).
 */
export class RowWrites {
  readonly #ledger: Ledger<RowWrites.Types>;
  #owner: RowWrites.Owner = RowWrites.UNOWNED;

  /** `sinks` answers where a row's invalidations are announced (each once). */
  constructor(sinks: RowWrites.Sinks) {
    this.#ledger = new Ledger<RowWrites.Types>(ROW_FIELDS, {
      sinks,
      name: "row-writes",
      takeBackContext: "the take-back of a refused row write",
      describe: (row) => ({ row: row.rawId }),
      live: () => this.#owner.live(),
      abandoned: (row, fields, error) => this.#owner.abandoned(row, fields, error),
    });
  }

  /**
   * Bind the ledger to its owner's lifecycle (the client that holds it): a
   * take-back due after the owner ended is not sent, and `owner.abandoned`
   * reports it. Unbound, the ledger is `RowWrites.UNOWNED` (always live).
   */
  bindOwner(owner: RowWrites.Owner): void {
    this.#owner = owner;
  }

  /**
   * Register a display copy. `first`: its values are the ones a write's base
   * reads first (the SDK's row, which holds every field the wire carries).
   * Returns the unregistration.
   */
  addDisplay(display: RowWrites.Display, rank: "first" | "last"): () => void {
    return this.#ledger.addDisplay(display, rank);
  }

  /**
   * Enter `change` as a write of `row` and SHOW it — synchronously, before
   * this returns: every display copy takes the new projection and each
   * changed field is announced as `actor` (attribute keys deferred when
   * `quietAttributes`). The write is sent with `send` (or `run`) and settled
   * with `accept` / `refuse`; until then it stays in the projection.
   */
  begin(row: RowLocalId, change: RowWrites.Change, actor: Actor, opts: RowWrites.Begin): RowWrites.Write {
    return this.#ledger.begin(row, change, actor, {
      correction: opts.correction,
      quiet: opts.quietAttributes,
      capture: opts.capture,
    });
  }

  /** Whether a write of `field` of `row` is in flight. */
  inFlight(row: RowLocalId, field: RowWrites.Field): boolean {
    return this.#ledger.inFlight(row, field);
  }

  /** The row's projection while it has writes in flight or reads open; `null` when idle. */
  project(row: RowLocalId): Option<RowWrites.Fields> {
    return this.#ledger.project(row);
  }

  /**
   * Send `write` through `transport`: the body is the projection of the
   * write's fields NOW (`attributes`: the whole projected bag), so it carries
   * every older write of those fields still in flight. Resolves with the
   * transport's answer; settle the write on it (`accept` / `refuse`).
   */
  send(write: RowWrites.Write, transport: RowWrites.Transport): Promise<RowWrites.Answer> {
    return this.#ledger.send(write, answered(transport));
  }

  /**
   * Send `writes` (of one row) as ONE PATCH through `transport`: the body is
   * the projection of all their fields now, and every one of them counts it
   * as its own send (a pending row's update split by who answers for each
   * part, sent whole once the row exists). Settle each on the answer.
   */
  sendAll(writes: readonly RowWrites.Write[], transport: RowWrites.Transport): Promise<RowWrites.Answer> {
    if (writes.length === 0) return Promise.resolve(RowWrites.ANSWERED);
    return this.#ledger.sendAll(writes, answered(transport)).then((answer) => answer ?? RowWrites.ANSWERED);
  }

  /**
   * `send`, then settle on the answer: accepted resolves with it; a refusal
   * or no answer takes the write back (`refuse`) and rejects with its
   * `RowWriteError("update")`, `context` leading its details.
   */
  run(write: RowWrites.Write, transport: RowWrites.Transport, context: string): Promise<RowWrites.Answer> {
    return this.#ledger.run(write, answered(transport), context);
  }

  /**
   * The server accepted `write`: every leading accepted write folds into the
   * row's base (issue order). Nothing is shown: the projection is unchanged.
   * Settling twice changes nothing.
   */
  accept(write: RowWrites.Write): void {
    this.#ledger.accept(write);
  }

  /**
   * The server refused `write` (or did not answer): it leaves the ledger, its
   * fields go back to the projection of the writes that remain (shown and
   * announced as the write's correction actor), and when a later send
   * carried its fields the surviving projection of them is PATCHed again
   * (while the owner is live — `bindOwner`). Returns the
   * `RowWriteError("update")` to reject with (`err` when it already is one).
   * Settling twice returns the same wrap and changes nothing.
   */
  refuse(write: RowWrites.Write, err: unknown, context: string): RowWriteError {
    return this.#ledger.refuse(write, err, context);
  }

  /**
   * Lay `fields` over the row's confirmed base — an authoritative answer
   * about those fields alone (a create that carried them, a parent's accepted
   * bag that carried a nested row's). The writes in flight keep replaying
   * over it; nothing is shown.
   */
  confirm(row: RowLocalId, fields: RowWrites.Fields): void {
    this.#ledger.confirm(row, fields);
  }

  /**
   * The row's life ended (its landed delete): every write of `row` in
   * flight is abandoned and every read open on it dropped (`Ledger.forget`)
   * — a late answer touches nothing and PATCHes no take-back; nothing is
   * shown. Answers the row's confirmed base (`null`: nothing was in flight),
   * the count abandoned, and `landed`: the bodies accepted late, in issue
   * order (the server holds them; the ledger no longer tracks them).
   */
  forget(row: RowLocalId): Ledger.Forgotten<RowWrites.Types> {
    return this.#ledger.forget(row);
  }

  /**
   * The deferred attribute announcements of `write` (`Begin.quietAttributes`):
   * one `RowDataChanged` per attribute key it changed, as `actor`. Emitted
   * once; a second call emits nothing.
   */
  announce(write: RowWrites.Write, actor: Actor): void {
    this.#ledger.announce(write, actor);
  }

  /** Open an authoritative read of `row` — before its request is sent; close it with `endRead`. */
  beginRead(row: RowLocalId): RowWrites.Read {
    return this.#ledger.beginRead(row);
  }

  /**
   * Close `read`. `confirmed` is the row as the server answered (every field
   * the read returns), `null` when the read failed or was dropped (nothing
   * changes). The writes accepted during the read that were sent after it
   * opened (its answer may predate them) lay over it in issue order — a
   * write sent before the read reached the server first, so the answer
   * already holds it — that becomes the row's base, and the new projection
   * is shown and announced as `actor`. Answers whether anything was shown.
   */
  endRead(read: RowWrites.Read, confirmed: Option<RowWrites.Fields>, actor: Actor): boolean {
    return this.#ledger.endRead(read, confirmed, actor);
  }

  /**
   * A multi-row write group (a rename: the label write and the formula
   * rewrites it causes): writes added to it are taken back together by
   * `refuseAll` (a refusal that sends nothing — the members were never sent).
   */
  group(): RowWrites.Group {
    return this.#ledger.group();
  }
}

/** `transport`, its answer defaulted: an implementer typed before
 *  `Backend.updateRow` answered resolves nothing — read as no refetch hint. */
function answered(transport: RowWrites.Transport): Ledger.Transport<RowWrites.Types> {
  return (body) => transport(body).then((answer: RowWrites.Answer | undefined) => answer ?? RowWrites.ANSWERED);
}

/** Types and helpers that go with `RowWrites`. */
export namespace RowWrites {
  /** A row field the ledger tracks. */
  export type Field = "label" | "icon" | "heightPx" | "formula" | "timeScale" | "attributes" | "lookupKey";

  /** Every tracked field. */
  export const FIELDS: readonly Field[] = Object.freeze([
    "label",
    "icon",
    "heightPx",
    "formula",
    "timeScale",
    "attributes",
    "lookupKey",
  ]);

  /**
   * A row's own fields as the row PATCH writes them (`null`: cleared; a field
   * left out is not part of the value). The wire's patch shape.
   */
  export type Fields = SheetsNetwork.PatchRow_PendingValidation;

  /**
   * An attribute merge laid over the projected bag: `deep` (a key it names
   * replaces that key's value, plain objects recursing — `mergeAttributes`,
   * an alias prop), `shallow` (each top-level key replaced whole — a display
   * attribute); `unset` keys are removed afterwards.
   */
  export type AttributeMerge = {
    readonly mode: "deep" | "shallow";
    readonly bag: JsonObject;
    readonly unset: readonly string[];
  };

  /**
   * One write's change: the fields it sets whole (`set`), and an attribute
   * merge over the projected bag (`null`: none). A write that sets
   * `attributes` whole takes no merge.
   */
  export type Change = { readonly set: Fields; readonly attributes: Option<AttributeMerge> };

  /**
   * The `RowWriteError` (step `"update"`, `PATCH /api/rows/{rowKey}`) a row
   * write's failure `err` rejects with: `err` itself when it is one;
   * otherwise `RowWriteError.after`'s (`context`, when not empty, leading the
   * details; a body that did not decode is `"malformed"`).
   */
  export function wrap(err: unknown, context: string): RowWriteError {
    const failed = requestRejection(err);
    if (failed instanceof RowWriteError) return failed;
    return RowWriteError.after(err, "update", "PATCH", ROUTE, context);
  }

  /** A change that sets `fields` whole. */
  export function set(fields: Fields): Change {
    return { set: fields, attributes: null };
  }

  /** A change that merges `bag` into the attributes (`mode`), removing `unset`. */
  export function mergeAttributes(mode: "deep" | "shallow", bag: JsonObject, unset: readonly string[]): Change {
    return { set: {}, attributes: { mode, bag, unset } };
  }

  /** The fields `change` writes. */
  export function fieldsOf(change: Change): Set<Field> {
    const fields = new Set<Field>();
    for (const f of FIELDS) if (change.set[f] !== undefined) fields.add(f);
    if (change.attributes != null) fields.add("attributes");
    return fields;
  }

  /** `change` laid over `fields`. */
  export function apply(fields: Fields, change: Change): Fields {
    const out: Fields = { ...fields, ...defined(change.set) };
    const merge = change.attributes;
    if (merge == null) return out;
    const bag = Json.object(out.attributes ?? null) ?? {};
    const merged: { [key: string]: Json } = merge.mode === "deep" ? deepMerge(bag, merge.bag) : { ...bag };
    if (merge.mode === "shallow") {
      for (const [key, value] of Object.entries(merge.bag)) if (value !== undefined) merged[key] = structuredClone(value);
    }
    for (const key of merge.unset) delete merged[key];
    return { ...out, attributes: merged };
  }

  /** `fields` restricted to `only` (a field `fields` lacks is `null`). */
  export function restrict(fields: Fields, only: ReadonlySet<Field>): Fields {
    const out: { -readonly [K in Field]?: Fields[K] } = {};
    for (const f of FIELDS) {
      if (!only.has(f)) continue;
      assign(out, f, fields[f] ?? null);
    }
    return out;
  }

  /**
   * What changed from `before` to `after`: the fields whose value differs
   * (`set`, each with its new value; `null` when cleared) and, for the
   * attribute bag, the top-level keys whose value differs. `null` when
   * nothing changed.
   */
  export function delta(before: Fields, after: Fields): Option<Delta> {
    const set: { -readonly [K in Field]?: Fields[K] } = {};
    let changed = false;
    for (const f of FIELDS) {
      if (f === "attributes") continue;
      if (!Object.hasOwn(after, f)) continue;
      const a = after[f] ?? null;
      if ((before[f] ?? null) === a) continue;
      assign(set, f, a);
      changed = true;
    }
    let attributes: Option<AttributeDelta> = null;
    if (Object.hasOwn(after, "attributes")) {
      const b = Json.object(before.attributes ?? null);
      const a = Json.object(after.attributes ?? null);
      const keys: string[] = [];
      for (const key of new Set([...Object.keys(b ?? {}), ...Object.keys(a ?? {})])) {
        if (!jsonEqual(b?.[key], a?.[key])) keys.push(key);
      }
      if (keys.length > 0 || (b == null) !== (a == null)) {
        attributes = { before: b, after: a, keys };
        changed = true;
      }
    }
    return changed ? { set, attributes } : null;
  }

  /** The attribute part of a `Delta`: the bag before and after, and the keys that differ. */
  export type AttributeDelta = {
    readonly before: Option<JsonObject>;
    readonly after: Option<JsonObject>;
    readonly keys: readonly string[];
  };

  /** What a show changes (`delta`): the non-attribute fields' new values and the attribute keys. */
  export type Delta = { readonly set: Fields; readonly attributes: Option<AttributeDelta> };

  /**
   * One display copy of rows (the SDK's `SheetRow.raw`, a sheet's
   * `RowMetadataStore`). `peek` answers the fields it holds for a row (`null`:
   * it holds no copy); `show` applies `delta` SILENTLY (the ledger announces)
   * and answers any extra `RowDataChanged` fields the change produced there
   * (an alias row's `props.<name>`).
   */
  export interface Display {
    peek(row: RowLocalId): Option<Fields>;
    show(row: RowLocalId, delta: Delta): readonly string[];
  }

  /** Where row `row`'s invalidations are announced: each sink once (a
   *  client's shared sink, or each holding sheet's own). */
  export type Sinks = (row: RowLocalId) => readonly InvalidationSink[];

  /** How `begin` enters a write. */
  export type Begin = {
    /** Who a take-back of the write is announced as. */
    readonly correction: Actor;
    /** Defer the attribute announcements (`announce` emits them). */
    readonly quietAttributes: boolean;
    /** The row's fields when no display holds a copy (`null`: none). */
    readonly capture: Option<() => Fields>;
  };

  /** The server's answer to a row PATCH. */
  export type Answer = { readonly shouldRefetchData: boolean };

  /** An answer with no refetch hint. */
  export const ANSWERED: Answer = Object.freeze({ shouldRefetchData: false });

  /** Send one row PATCH body; resolves with the answer, rejects on a refusal or no answer. */
  export type Transport = (body: Fields) => Promise<Answer>;

  /** One write in flight (compared by identity): its row (`key`) and fields. */
  export type Write = Ledger.Write<Types>;

  /** One authoritative read in flight. */
  export type Read = Ledger.Read<Types>;

  /** A multi-row write group (`RowWrites.group`). */
  export type Group = Ledger.Group<Types>;

  /** The type bundle of the row-field instance of `Ledger`. */
  export interface Types extends Ledger.Types {
    readonly key: RowLocalId;
    readonly change: Change;
    readonly fields: Fields;
    readonly field: Field;
    readonly delta: Delta;
    readonly answer: Answer;
    readonly error: RowWriteError;
  }

  /**
   * The lifecycle a ledger's take-backs follow (`RowWrites.bindOwner`):
   * `live` answers whether the owner may still send; a take-back due once it
   * answers `false` is not sent and `abandoned` reports it (`error`: the
   * refusal it would have corrected).
   */
  export type Owner = {
    readonly live: () => boolean;
    readonly abandoned: (row: RowLocalId, fields: ReadonlySet<Field>, error: RowWriteError) => void;
  };

  /** No owner: always live (a test ledger, a standalone sheet's). */
  export const UNOWNED: Owner = Object.freeze({
    live: () => true,
    abandoned: () => {},
  });
}

/** `fields` without its `undefined` entries. */
function defined(fields: RowWrites.Fields): RowWrites.Fields {
  const out: { -readonly [K in RowWrites.Field]?: RowWrites.Fields[K] } = {};
  for (const f of RowWrites.FIELDS) {
    const v = fields[f];
    if (v !== undefined) assign(out, f, v);
  }
  return out;
}

/** Set `out[field]` to `value` (a value `fields` holds for `field`). */
function assign<K extends RowWrites.Field>(
  out: { -readonly [F in RowWrites.Field]?: RowWrites.Fields[F] },
  field: K,
  value: RowWrites.Fields[K],
): void {
  out[field] = value;
}

/** `patch` deep-merged into `target`: a key it names replaces that key's value, plain objects recursing. */
function deepMerge(target: JsonObject, patch: JsonObject): { [key: string]: Json } {
  const merged: { [key: string]: Json } = { ...target };
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) continue;
    const before = merged[key] ?? null;
    merged[key] = Json.isObject(value) && Json.isObject(before) ? deepMerge(before, value) : structuredClone(value);
  }
  return merged;
}

/** Structural equality of two JSON values (`undefined` equals `null`). */
function jsonEqual(a: Option<Json> | undefined, b: Option<Json> | undefined): boolean {
  const x = a ?? null;
  const y = b ?? null;
  if (x === y) return true;
  if (x === null || y === null || typeof x !== "object" || typeof y !== "object") return false;
  if (Array.isArray(x) || Array.isArray(y)) {
    if (!Array.isArray(x) || !Array.isArray(y) || x.length !== y.length) return false;
    return x.every((v, i) => jsonEqual(v, y[i]));
  }
  const xo = Json.object(x);
  const yo = Json.object(y);
  if (xo == null || yo == null) return false;
  const keys = new Set([...Object.keys(xo), ...Object.keys(yo)]);
  for (const key of keys) if (!jsonEqual(xo[key], yo[key])) return false;
  return true;
}
